// ── WALL CLEANUP ─────────────────────────────────────────────────────────────
// Post-processing for wall geometry, whatever produced it — the image extractor,
// a `wall`-class model, or an imported vector drawing.
//
// Detected walls are ALMOST right and that is the problem: a wall a third of a
// degree off horizontal, or an endpoint two pixels short of its neighbour, looks
// correct to the eye and is completely wrong to the algorithms downstream. Room
// detection floods through a two-pixel crack and merges the kitchen with the
// garden; an opening fails to find its wall; two nearly-coincident walls
// z-fight. Every one of those bugs is a rounding error that was never cleaned up.
//
// Three passes, in this order — the order matters:
//
//   1. ORTHOGONALIZE   snap near-axis walls to exactly axis-aligned.
//   2. WELD            merge endpoints that nearly touch into one shared vertex.
//   3. MERGE           fuse overlapping collinear walls into one.
//
// Orthogonalizing first is what makes welding safe: once every wall is exactly
// horizontal or vertical, endpoints can be welded per-axis, which snaps them
// together WITHOUT tilting anything. Welding raw diagonal walls would move each
// endpoint independently and could rotate a wall slightly — reintroducing the
// very error step 1 removed. And merging last means it sees the welded
// coordinates, so walls that should be one are now exactly collinear rather than
// a hair apart.
//
// ══ UNITS ═══════════════════════════════════════════════════════════════════
//
// Unit-agnostic. Pass pixel walls with a pixel `weldRadius`, or metre walls with
// a metre one. The defaults are pixel-scaled; callers working in metres should
// pass their own.

const DEFAULTS = {
  angleToleranceDeg: 5,   // how far off-axis a wall may be and still be snapped
  weldRadius: 20,         // endpoints closer than this become one vertex
  minLength: 1,           // shorter than this is not a wall
};

/** Segment as the rest of the app holds it, from {x1,y1,x2,y2}. */
const lengthOf = w => Math.hypot(w.x2 - w.x1, w.y2 - w.y1);

/**
 * PASS 1 — force near-axis walls onto their axis.
 *
 * A wall within `angleToleranceDeg` of horizontal has its two y values replaced
 * by their mean; near-vertical, its two x values. The MEAN rather than one
 * endpoint's value, so the wall pivots about its own centre instead of swinging
 * one end across the drawing — which would drag it away from whatever it was
 * meeting.
 *
 * Walls that are genuinely diagonal (a bay, a canted corner) are left alone. The
 * tolerance is what separates "drawn at an angle" from "meant to be straight".
 */
export function orthogonalize(walls, angleToleranceDeg = DEFAULTS.angleToleranceDeg) {
  const tol = Math.tan((angleToleranceDeg * Math.PI) / 180);
  return walls.map((w) => {
    const dx = Math.abs(w.x2 - w.x1);
    const dy = Math.abs(w.y2 - w.y1);
    if (dx === 0 && dy === 0) return { ...w };

    // Compare the off-axis drop against the run: dy/dx <= tan(tolerance) is the
    // same test as the angle, without a trig call per wall.
    if (dy <= dx * tol) {
      const y = (w.y1 + w.y2) / 2;
      return { ...w, y1: y, y2: y };
    }
    if (dx <= dy * tol) {
      const x = (w.x1 + w.x2) / 2;
      return { ...w, x1: x, x2: x };
    }
    return { ...w };
  });
}

/**
 * Clusters 1-D values, replacing each with its cluster's mean.
 *
 * Single-linkage: a value joins the run while it is within `radius` of the
 * PREVIOUS value, so a line of endpoints each 15 px apart welds into one even
 * though the ends are 60 px apart. That is usually what you want on a drawing —
 * they were all meant to be the same coordinate — but it does mean the radius
 * must stay well below the smallest real wall spacing.
 */
function weldAxis(values, radius) {
  const sorted = [...new Set(values)].sort((a, b) => a - b);
  const map = new Map();
  let i = 0;
  while (i < sorted.length) {
    let j = i + 1;
    while (j < sorted.length && sorted[j] - sorted[j - 1] <= radius) j++;
    const group = sorted.slice(i, j);
    const mean = group.reduce((s, v) => s + v, 0) / group.length;
    for (const v of group) map.set(v, mean);
    i = j;
  }
  return map;
}

/**
 * PASS 2 — weld endpoints that nearly touch into one shared vertex.
 *
 * Done PER AXIS rather than by clustering 2-D points, and that is the whole
 * trick: snapping x values among themselves and y values among themselves
 * cannot tilt an axis-aligned wall, because both of its endpoints share the same
 * coordinate on one axis and therefore land in the same cluster. Point-based
 * welding would move each end independently and could rotate the wall.
 *
 * This is what actually closes rooms. A corner where two walls stop 3 px apart
 * leaves a gap that a flood fill escapes through, and the two rooms either side
 * merge into one. After welding they share a vertex exactly.
 */
export function weldNodes(walls, weldRadius = DEFAULTS.weldRadius) {
  if (!walls.length) return [];
  const xs = walls.flatMap(w => [w.x1, w.x2]);
  const ys = walls.flatMap(w => [w.y1, w.y2]);
  const mapX = weldAxis(xs, weldRadius);
  const mapY = weldAxis(ys, weldRadius);

  return walls.map(w => ({
    ...w,
    x1: mapX.get(w.x1), y1: mapY.get(w.y1),
    x2: mapX.get(w.x2), y2: mapY.get(w.y2),
  }));
}

/**
 * PASS 3 — fuse overlapping collinear walls into one.
 *
 * After welding, walls that were "nearly the same wall" are exactly collinear,
 * so they can be grouped by axis and position and their spans unioned. Handles
 * three cases that all arrive from real drawings:
 *
 *   - the SAME wall found twice (two rooms each contributing their shared edge)
 *   - one wall fully INSIDE another (a partition traced over the shell)
 *   - two walls OVERLAPPING end to end
 *
 * Spans are joined only when they overlap or touch — never across a gap. A gap
 * in a wall is a doorway, and closing it would delete the opening.
 *
 * Thickness comes out as the MAXIMUM of the merged pieces: a wall drawn thick in
 * one place and thin in another is one wall of the thicker weight, and taking
 * the max never leaves a hole where the thicker piece was.
 */
export function mergeCollinearWalls(walls, minLength = DEFAULTS.minLength) {
  const horizontal = [], vertical = [], diagonal = [];
  for (const w of walls) {
    if (w.y1 === w.y2 && w.x1 !== w.x2) horizontal.push(w);
    else if (w.x1 === w.x2 && w.y1 !== w.y2) vertical.push(w);
    else if (lengthOf(w) >= minLength) diagonal.push(w);   // left untouched
  }

  // A merged run, back in {x1,y1,x2,y2}. The fixed coordinate is written to BOTH
  // ends, which is what keeps the result exactly axis-aligned.
  const emit = (run, fixed, isH) => (isH
    ? { ...run.src, x1: run.lo, y1: fixed, x2: run.hi, y2: fixed, thickness: run.thickness, openings: run.openings }
    : { ...run.src, x1: fixed, y1: run.lo, x2: fixed, y2: run.hi, thickness: run.thickness, openings: run.openings });

  const fuse = (group, isH) => {
    const byLine = new Map();
    for (const w of group) {
      const fixed = isH ? w.y1 : w.x1;
      if (!byLine.has(fixed)) byLine.set(fixed, []);
      byLine.get(fixed).push(w);
    }

    const result = [];
    for (const [fixed, list] of byLine) {
      const spans = list.map(w => ({
        lo: Math.min(isH ? w.x1 : w.y1, isH ? w.x2 : w.y2),
        hi: Math.max(isH ? w.x1 : w.y1, isH ? w.x2 : w.y2),
        thickness: w.thickness || 0,
        openings: w.openings || [],
        src: w,
      })).sort((p, q) => p.lo - q.lo);

      let cur = null;
      for (const s of spans) {
        if (cur && s.lo <= cur.hi) {          // overlap or touch — never a gap
          cur.hi = Math.max(cur.hi, s.hi);
          cur.thickness = Math.max(cur.thickness, s.thickness);
          cur.openings = cur.openings.concat(s.openings);
        } else {
          if (cur) result.push(emit(cur, fixed, isH));
          cur = { ...s, openings: [...s.openings] };
        }
      }
      if (cur) result.push(emit(cur, fixed, isH));
    }
    return result;
  };

  return [
    ...fuse(horizontal, true),
    ...fuse(vertical, false),
    ...diagonal,
  ].filter(w => lengthOf(w) >= minLength);
}

/**
 * All three passes, in the order that makes them safe.
 *
 * @param {Array<{x1,y1,x2,y2,thickness?,openings?}>} walls
 * @param {object} [opts]
 * @param {number} [opts.angleToleranceDeg=5]  snap walls within this of an axis
 * @param {number} [opts.weldRadius=20]        endpoints closer than this merge
 * @param {number} [opts.minLength=1]          discard shorter results
 * @returns {Array} cleaned walls, same shape as the input
 */
export function cleanupWalls(walls, opts = {}) {
  const { angleToleranceDeg, weldRadius, minLength } = { ...DEFAULTS, ...opts };
  if (!Array.isArray(walls) || !walls.length) return [];
  let out = orthogonalize(walls, angleToleranceDeg);
  out = weldNodes(out, weldRadius);
  out = mergeCollinearWalls(out, minLength);
  return out;
}

// ── adapters ─────────────────────────────────────────────────────────────────
// The extractor holds a wall as {axis, at, from, to} because that is the natural
// shape for a row/column scan. These convert to and from the {x1,y1,x2,y2} form
// the cleanup works in, so neither side has to know about the other's layout.

export function segmentsToWalls(segments) {
  return (segments || []).map(s => (s.axis === 'h'
    ? { x1: s.from, y1: s.at, x2: s.to, y2: s.at, thickness: s.thickness, openings: s.openings }
    : { x1: s.at, y1: s.from, x2: s.at, y2: s.to, thickness: s.thickness, openings: s.openings }));
}

export function wallsToSegments(walls) {
  return (walls || []).map((w) => {
    const horizontal = w.y1 === w.y2;
    return horizontal
      ? { axis: 'h', at: w.y1, from: Math.min(w.x1, w.x2), to: Math.max(w.x1, w.x2), thickness: w.thickness, openings: w.openings || [] }
      : { axis: 'v', at: w.x1, from: Math.min(w.y1, w.y2), to: Math.max(w.y1, w.y2), thickness: w.thickness, openings: w.openings || [] };
  });
}
